/* eslint-disable no-undef */
import $ from 'jquery';
window.jQuery = window.$ = $;
// Prevent Chrome [Violation] Permissions policy violation for 'unload'
if ($.event && $.event.special) {
  $.event.special.unload = {
    setup: function () {
      // Returning true prevents jQuery from calling window.addEventListener('unload')
      return true;
    },
  };
}
require('signalr');

import { getPortalServerIp, getE2EEncryptionEnabledFlag } from '../storage';
import { getDecryptedConversationKeyFromCache } from './conversationKeyCache';
import { DecryptMessage } from './e2ee';

// Convert ISO date to Microsoft /Date(...)/ format
export function convertToMicrosoftDateFormat(dateStr) {
  if (!dateStr) return null;
  if (typeof dateStr === 'string' && dateStr.startsWith('/Date(')) {
    return dateStr;
  }
  const date = new Date(dateStr);
  const timestamp = date.getTime();
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absOffset = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absOffset / 60)).padStart(2, '0');
  const minutes = String(absOffset % 60).padStart(2, '0');
  return `/Date(${timestamp}${sign}${hours}${minutes})/`;
}

class SignalRService {
  constructor() {
    this.identificationId = null;
    this.connection = null;
    this.hub = null;

    this.isConnected = false;
    this.isStarting = false;
    this.startPromise = null;

    this.joinedConversationId = null;
    this.pendingJoin = null;

    this.connectionListeners = new Set();
    this.joinListeners = new Set();
    this.messageEventListeners = new Set();
    this.summaryEventListeners = new Set();
    this.reactionEventListeners = new Set();
    this.callEventListeners = new Set();
    this.deliveredEventListeners = new Set();
    this.readEventListeners = new Set();

    this.reconnectAttempts = 0;
    this.retryTimeoutId = null;
    this.isManuallyStopped = false;
    this.isReconnecting = false;
    this.lastMessageTime = Date.now();

    // 1. Browser Online/Offline detection
    window.addEventListener('offline', () => {
      console.warn('[SignalR] Network offline');
      window.dispatchEvent(new CustomEvent('signalr-failed'));
      this.isConnected = false;
      this.emitConnection(false);
    });

    window.addEventListener('online', () => {
      console.log('[SignalR] Network back online — reconnecting');
      if (this.identificationId) {
        this.forceReconnect();
      }
    });

    // 2. On Page Refresh or Tab Close: cleanly leave conversation & stop connection
    window.addEventListener('beforeunload', () => {
      if (this.joinedConversationId && this.connection?.state === 1) {
        try {
          this.hub?.invoke('leaveConversation', this.joinedConversationId);
        } catch (e) {
          // ignore synchronous unload errors
        }
      }
      try {
        this.connection?.stop();
      } catch (e) {
        // ignore
      }
    });

    // 3. Tab Visibility (When user wakes PC, unlocks screen, or switches back to tab)
    // NOTE: Does NOT disconnect when tab is hidden or blurred; only checks & reconnects if it dropped!
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        const state = this.connection ? this.connection.state : 4;
        if (
          this.identificationId &&
          !this.isManuallyStopped &&
          (!this.isConnected || state !== 1)
        ) {
          console.log(
            '[SignalR] Tab focused & connection inactive (state=' +
              state +
              '). Reconnecting...'
          );
          this.forceReconnect();
        }
      }
    });
  }

  // ===============================
  // Event Subscriptions
  // ===============================
  subscribeConnection(cb) {
    this.connectionListeners.add(cb);
    return () => this.connectionListeners.delete(cb);
  }

  subscribeJoin(cb) {
    this.joinListeners.add(cb);
    return () => this.joinListeners.delete(cb);
  }

  subscribeReceiveMessage(cb) {
    this.messageEventListeners.add(cb);
    return () => this.messageEventListeners.delete(cb);
  }

  subscribeConversationSummary(cb) {
    this.summaryEventListeners.add(cb);
    return () => this.summaryEventListeners.delete(cb);
  }

  subscribeReaction(cb) {
    this.reactionEventListeners.add(cb);
    return () => this.reactionEventListeners.delete(cb);
  }

  subscribeIncomingCall(cb) {
    this.callEventListeners.add(cb);
    return () => this.callEventListeners.delete(cb);
  }

  subscribeMessageDelivered(cb) {
    this.deliveredEventListeners.add(cb);
    return () => this.deliveredEventListeners.delete(cb);
  }

  subscribeMessageRead(cb) {
    this.readEventListeners.add(cb);
    return () => this.readEventListeners.delete(cb);
  }

  emitConnection(status) {
    this.connectionListeners.forEach((cb) => cb(status));
  }

  emitJoin(conversationId) {
    this.joinListeners.forEach((cb) => cb(conversationId));
  }

  // ===============================
  // Connection Management
  // ===============================
  async start(identificationId) {
    console.log('[SignalR] start() called with ID:', identificationId);
    this.isManuallyStopped = false;

    if (this.connection && this.isConnected && this.connection.state === 1) {
      console.log('[SignalR] Already connected');
      this.emitConnection(true);
      return;
    }

    if (this.isStarting) {
      console.log('[SignalR] Already starting, returning promise');
      return this.startPromise;
    }

    clearTimeout(this.retryTimeoutId);
    this.identificationId = identificationId;

    if (this.connection) {
      try {
        this.connection.stop();
      } catch (e) {
        console.warn('Old SignalR connection stop ignored');
      }
      this.connection = null;
      this.hub = null;
    }

    this.isManuallyStopped = false;

    let baseUrl = getPortalServerIp();

    // Fallback: If sessionStorage returned 'https://null', read it directly from localStorage
    if (!baseUrl || baseUrl === 'https://null') {
      try {
        const reg = JSON.parse(localStorage.getItem('@Squre:RegisterDevice'));
        const ip = reg?.RegisterDeviceResult?.Configuration?.PortalServerIp;
        if (ip) {
          baseUrl = ip.startsWith('http') ? ip : `https://${ip}`;
          sessionStorage.setItem('@Squre:PortalServerIp', ip);
        }
      } catch (e) {}
    }

    if (!baseUrl || baseUrl === 'https://null') {
      console.warn(
        '[SignalR] Portal server IP not available yet. Retrying in 3s...'
      );
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = setTimeout(() => {
        if (this.identificationId && !this.isManuallyStopped) {
          this.start(this.identificationId);
        }
      }, 3000);
      return;
    }

    this.isStarting = true;

    this.startPromise = new Promise((resolve) => {
      this.connection = $.hubConnection(`${baseUrl}/Chat/signalr`, {
        qs: { identificationId },
        useDefaultPath: false,
      });

      this.hub = this.connection.createHubProxy('chatHub');

      this.connection.received(() => {
        this.lastMessageTime = Date.now();
      });

      // Clear duplicate listeners
      this.hub.off('receiveMessage');
      this.hub.off('conversationSummaryUpdated');
      this.hub.off('incomingCallEvent');
      this.hub.off('reactionMessage');

      // 1. Incoming Call Event
      this.hub.on('incomingCallEvent', (res) => {
        console.log('[SignalR] incomingCallEvent:', res);
        this.callEventListeners.forEach((cb) => cb(res));
      });

      // 2. Receive Message Event
      this.hub.on('receiveMessage', async (res) => {
        this.lastMessageTime = Date.now();
        console.log('[SignalR] Message received:', res);

        try {
          let messages = Array.isArray(res.StaffMessage)
            ? res.StaffMessage
            : [res.StaffMessage];

          // Decrypt in-memory if E2EE is enabled
          if (getE2EEncryptionEnabledFlag()) {
            const cachedKeys = getDecryptedConversationKeyFromCache(
              res.ConversationId
            );
            if (cachedKeys?.key && cachedKeys?.iv) {
              for (const msg of messages) {
                if (msg.Message) {
                  msg.Message = await DecryptMessage(
                    msg.Message,
                    cachedKeys.iv,
                    cachedKeys.key
                  );
                }
                if (msg.ReferencedMessage?.Message) {
                  msg.ReferencedMessage.Message = await DecryptMessage(
                    msg.ReferencedMessage.Message,
                    cachedKeys.iv,
                    cachedKeys.key
                  );
                }
              }
            }
          }

          // Normalize dates
          const normalizedMessages = messages.map((msg) => ({
            ...msg,
            MessageDateTime: convertToMicrosoftDateFormat(msg.MessageDateTime),
          }));

          res.StaffMessage = Array.isArray(res.StaffMessage)
            ? normalizedMessages
            : normalizedMessages[0];

          // Notify UI listeners (Chat & Conversation Left List)
          this.messageEventListeners.forEach((cb) => cb(res));
        } catch (err) {
          console.error('[SignalR] Error processing receiveMessage:', err);
        }
      });

      // 3. Conversation Summary Updated Event
      this.hub.on('conversationSummaryUpdated', async (data) => {
        console.log('[SignalR] conversationSummaryUpdated:', data);
        try {
          const summaries = Array.isArray(data) ? data : [data];

          // Decrypt preview message if E2EE is enabled
          if (getE2EEncryptionEnabledFlag()) {
            for (const item of summaries) {
              if (item.StaffMessage?.Message) {
                const cachedKeys = getDecryptedConversationKeyFromCache(
                  item.ConversationId
                );
                if (cachedKeys?.key && cachedKeys?.iv) {
                  item.StaffMessage.Message = await DecryptMessage(
                    item.StaffMessage.Message,
                    cachedKeys.iv,
                    cachedKeys.key
                  );
                }
              }
            }
          }

          this.summaryEventListeners.forEach((cb) => cb(summaries));
        } catch (err) {
          console.error(
            '[SignalR] Error processing conversationSummaryUpdated:',
            err
          );
        }
      });

      // 4. Reaction Message Event
      this.hub.on('reactionMessage', (res) => {
        console.log('[SignalR] reactionMessage received:', res);
        this.reactionEventListeners.forEach((cb) => cb(res));
      });

      // 5. Message Delivered Event
      this.hub.on('messageDelivered', (data) => {
        console.log('[SignalR] MESSAGE DELIVERED EVENT:', data);
        this.deliveredEventListeners.forEach((cb) => cb(data));
      });

      // 6. Message Read Event
      this.hub.on('messageRead', (data) => {
        console.log('[SignalR] MESSAGE READ EVENT:', data);
        this.readEventListeners.forEach((cb) => cb(data));
      });

      // Connection LifeCycle
      this.connection
        .start()
        .done(() => {
          this.isConnected = true;
          this.isStarting = false;
          this.reconnectAttempts = 0;
          this.isReconnecting = false;
          console.log('[SignalR] Connected successfully');
          this.emitConnection(true);
          resolve();
        })
        .fail((err) => {
          console.error('[SignalR] Connection failed:', err);
          // Dispatch failure event for UI badge
          window.dispatchEvent(new CustomEvent('signalr-failed'));
          this.isConnected = false;
          this.isStarting = false;
          this.startPromise = null;
          this.emitConnection(false);

          // Continuous retry loop: keeps retrying every 5 seconds until connected
          if (!this.isManuallyStopped && this.identificationId) {
            this.reconnectAttempts++;
            console.log(
              `[SignalR] Retrying connection in 5s (Attempt #${this.reconnectAttempts})...`
            );
            clearTimeout(this.retryTimeoutId);
            this.retryTimeoutId = setTimeout(() => {
              this.start(this.identificationId);
            }, 5000);
          }
          resolve();
        });

      this.connection.reconnected(() => {
        console.log('[SignalR] Reconnected successfully');
        this.isConnected = true;
        this.isReconnecting = false;
        this.emitConnection(true);
      });

      this.connection.reconnecting(() => {
        console.warn('[SignalR] Reconnecting...');
        // Dispatch failure event so badge appears during reconnection
        window.dispatchEvent(new CustomEvent('signalr-failed'));
        this.isConnected = false;
        this.emitConnection(false);
      });

      this.connection.disconnected(() => {
        if (this.isManuallyStopped) {
          console.log('[SignalR] Manually stopped — no reconnect');
          return;
        }

        // Dispatch failure event so badge appears
        window.dispatchEvent(new CustomEvent('signalr-failed'));
        this.isConnected = false;
        this.emitConnection(false);
        this.emitJoin(null);

        if (!navigator.onLine) {
          console.warn('[SignalR] Network offline — waiting for online event');
          return;
        }

        // Continuous retry loop on unexpected disconnect
        this.reconnectAttempts++;
        console.log(
          `[SignalR] Disconnected. Retrying in 5s (Attempt #${this.reconnectAttempts})...`
        );
        clearTimeout(this.retryTimeoutId);
        this.retryTimeoutId = setTimeout(() => {
          if (this.identificationId && !this.isManuallyStopped) {
            this.connection = null;
            this.hub = null;
            this.startPromise = null;
            this.isStarting = false;
            this.start(this.identificationId);
          }
        }, 5000);
      });
    });

    return this.startPromise;
  }

  forceReconnect() {
    if (this.isManuallyStopped || !navigator.onLine)
      return Promise.resolve(false);
    clearTimeout(this.retryTimeoutId);

    if (this.isReconnecting) {
      return this.startPromise || Promise.resolve();
    }

    this.isReconnecting = true;
    try {
      this.connection?.stop();
    } catch (e) {
      console.warn('Old SignalR connection stop ignored');
    }

    this.connection = null;
    this.hub = null;
    this.startPromise = null;
    this.isStarting = false;
    this.isConnected = false;
    this.emitConnection(false);

    if (this.identificationId) {
      return this.start(this.identificationId).finally(() => {
        this.isReconnecting = false;
      });
    }

    this.isReconnecting = false;
    return Promise.resolve();
  }

  // ===============================
  // Safe Hub Invocation
  // ===============================
  async safeInvoke(method, ...args) {
    // If logging out or already manually stopped, do not invoke
    if (this.isManuallyStopped) {
      return Promise.resolve(null);
    }

    if (this.isStarting && this.startPromise) {
      await this.startPromise;
    }

    if (!this.hub && this.startPromise) {
      await this.startPromise;
    }

    if (!this.isConnected) {
      await new Promise((resolve) => {
        const unsubscribe = this.subscribeConnection((status) => {
          if (status) {
            unsubscribe();
            resolve();
          }
        });
      });
    }

    return new Promise((resolve, reject) => {
      try {
        this.hub
          .invoke(method, ...args)
          .done(resolve)
          .fail((error) => {
            console.warn(`[SignalR] Invoke error (${method}):`, error);
            if (
              error?.message?.includes(
                'Connection has not been fully initialized'
              )
            ) {
              setTimeout(async () => {
                try {
                  const result = await this.safeInvoke(method, ...args);
                  resolve(result);
                } catch (retryErr) {
                  reject(retryErr);
                }
              }, 500);
            } else {
              reject(error || new Error(`SignalR invoke failed: ${method}`));
            }
          });
      } catch (err) {
        reject(err);
      }
    });
  }

  // ===============================
  // Join & Leave Conversation (Race Safe)
  // ===============================
  async joinConversation(conversationId) {
    if (!conversationId) return;

    if (
      this.joinedConversationId === conversationId ||
      this.pendingJoin === conversationId
    ) {
      return;
    }

    this.pendingJoin = conversationId;

    try {
      if (this.joinedConversationId) {
        await this.safeInvoke('leaveConversation', this.joinedConversationId);
        console.log('[SignalR] Left conversation:', this.joinedConversationId);
      }

      // Check if user clicked another chat while leave was pending
      if (this.pendingJoin !== conversationId) {
        console.log(
          '[SignalR] Join ignored (outdated request):',
          conversationId
        );
        return;
      }

      await this.safeInvoke('joinConversation', conversationId);

      if (this.pendingJoin === conversationId) {
        this.joinedConversationId = conversationId;
        this.emitJoin(conversationId);
        console.log(
          '[SignalR] Successfully joined conversation:',
          conversationId
        );
      }
    } catch (err) {
      console.error(
        '[SignalR] Failed to join conversation:',
        conversationId,
        err
      );
      this.emitJoin(null);
      throw err;
    }
  }

  async leaveConversation(conversationId) {
    if (!conversationId) return;

    // If logging out, already stopped, or socket is closed, clear local state and return cleanly
    // (SignalR server automatically removes disconnected clients from all groups)
    if (
      this.isManuallyStopped ||
      !this.isConnected ||
      !this.connection ||
      this.connection.state !== 1
    ) {
      if (this.joinedConversationId === conversationId) {
        this.joinedConversationId = null;
        this.emitJoin(null);
      }
      return;
    }

    try {
      await this.safeInvoke('leaveConversation', conversationId);
      console.log('[SignalR] Successfully left conversation:', conversationId);
      if (this.joinedConversationId === conversationId) {
        this.joinedConversationId = null;
        this.emitJoin(null);
      }
    } catch (err) {
      // If disconnected while leave was in-flight during logout/navigation, ignore harmless error
      if (
        err?.message?.includes('disconnected before invocation result') ||
        this.isManuallyStopped
      ) {
        if (this.joinedConversationId === conversationId) {
          this.joinedConversationId = null;
          this.emitJoin(null);
        }
        return;
      }
      console.warn(
        '[SignalR] Leave conversation ignored:',
        conversationId,
        err?.message || err
      );
    }
  }

  // ===============================
  // Send Message Methods (SignalR Hub)
  // ===============================
  async sendMessageUsingContactInformation(conversationId, chatRequest) {
    console.log('[SignalR] Hub Invoke: sendMessageUsingContactInformation');
    console.log('[SignalR] conversationId:', conversationId);
    console.log('[SignalR] chatRequest:', chatRequest);

    return this.safeInvoke(
      'sendMessageUsingContactInformation',
      conversationId,
      chatRequest
    );
  }

  async sendMessageUsingFilter(conversationGuidId, chatGroupRequest) {
    console.log('[SignalR] Hub Invoke: sendMessageUsingFilter');
    console.log('[SignalR] conversationId:', conversationGuidId);
    console.log('[SignalR] chatGroupRequest:', chatGroupRequest);

    return this.safeInvoke(
      'sendMessageUsingFilter',
      conversationGuidId,
      chatGroupRequest
    );
  }

  async sendReactionMessage(conversationGuidId, reactionDataRequest) {
    try {
      console.log('Call messageReactionRequest method using SignalR');
      console.log('conversationId', conversationGuidId);
      console.log('messageReactionRequest', reactionDataRequest);

      const result = await this.safeInvoke(
        'updateMessageReaction',
        conversationGuidId,
        reactionDataRequest
      );

      return result;
    } catch (error) {
      console.error('Error in sendReactionMessage:', error);
      // throw error; // rethrow if you want caller to handle it
    }
  }

  async markAllMessageReceived(conversationGuid, request) {
    try {
      console.log('Call markAllMessageReceived method using SignalR');
      console.log('conversationId', conversationGuid);
      console.log('request', request);

      const result = await this.safeInvoke(
        'markAllMessageReceived',
        conversationGuid,
        request
      );

      return result;
    } catch (error) {
      console.error('Error in markAllMessageReceived:', error);
    }
  }

  async markAllMessageRead(conversationGuid, request) {
    try {
      console.log('Call markAllMessageRead method using SignalR');
      console.log('conversationId', conversationGuid);
      console.log('request', request);

      const result = await this.safeInvoke(
        'markAllMessageRead',
        conversationGuid,
        request
      );

      return result;
    } catch (error) {
      console.error('Error in markAllMessageRead:', error);
    }
  }

  // ===============================
  // Logout / Cleanup
  // ===============================

  async logout() {
    this.isManuallyStopped = true;
    clearTimeout(this.retryTimeoutId);
    clearInterval(this.healthInterval);
    clearInterval(this.sleepCheckInterval);
    this.pendingJoin = null;

    // Clear joined conversation ID immediately to prevent unmount effects from racing
    const conversationToLeave = this.joinedConversationId;
    this.joinedConversationId = null;
    this.emitJoin(null);

    // Attempt to notify server if socket is still active
    if (
      conversationToLeave &&
      this.isConnected &&
      this.connection?.state === 1
    ) {
      try {
        await this.hub?.invoke('leaveConversation', conversationToLeave);
        console.log(
          '[SignalR] Left conversation on logout:',
          conversationToLeave
        );
      } catch (err) {
        // Safe to ignore on logout
      }
    }

    if (this.connection) {
      try {
        this.connection.stop();
      } catch (err) {
        console.warn('SignalR stop ignored:', err);
      }
    }

    this.connectionListeners.clear();
    this.joinListeners.clear();
    this.messageEventListeners.clear();
    this.summaryEventListeners.clear();
    this.reactionEventListeners.clear();
    this.callEventListeners.clear();

    this.connection = null;
    this.hub = null;
    this.startPromise = null;
    this.isStarting = false;
    this.isConnected = false;
    this.identificationId = null;
    this.reconnectAttempts = 0;

    this.emitConnection(false);
    console.log('[SignalR] Logout completed cleanly');
  }
}

export default new SignalRService();
